import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, CreateUserData, UpdateUserData } from '../../services/userService';
import { User, UserRole } from '../../types';
import { PermissionGuard } from '../common/PermissionGuard';
import { PERMISSIONS } from '../../utils/permissions';

const ds = {
  bg: '#faf7ee',
  card: '#ffffff',
  elevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#fbbf24',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  textDim: '#e3dcc4',
  borderNormal: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  borderActive: 'rgba(217,119,6,0.35)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const roleBadgeStyle = (role: string): React.CSSProperties => {
  const configs: Record<string, { bg: string; color: string }> = {
    SUPER_ADMIN: { bg: 'rgba(180,100,220,0.15)', color: '#c880f0' },
    ADMIN: { bg: 'rgba(220,80,80,0.15)', color: '#e08080' },
    MANAGER: { bg: 'rgba(80,140,220,0.15)', color: '#80aae0' },
    ACCOUNTANT: { bg: 'rgba(80,200,120,0.15)', color: '#80d0a0' },
    SALES: { bg: 'rgba(92,78,51,0.25)', color: '#d97706' },
    WAREHOUSE: { bg: 'rgba(220,140,60,0.15)', color: '#d8905a' },
    TECHNICIAN: { bg: 'rgba(100,120,220,0.15)', color: '#90a0e0' },
    USER: { bg: 'rgba(120,106,85,0.15)', color: '#6b6a5c' },
  };
  const conf = configs[role] || configs.USER;
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 20,
    background: conf.bg,
    color: conf.color,
    fontFamily: ds.fontLabel,
    fontSize: 9,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  };
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: `1px solid ${ds.borderSubtle}`,
  borderRadius: 4,
  color: ds.textSecondary,
  fontFamily: ds.fontBody,
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: ds.fontLabel,
  fontSize: 9,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: ds.textMuted,
  marginBottom: 5,
};

const thStyle: React.CSSProperties = {
  padding: '12px 20px',
  textAlign: 'left',
  fontFamily: ds.fontLabel,
  fontSize: 9,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: ds.textMuted,
  borderBottom: `1px solid ${ds.borderNormal}`,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 20px',
  fontFamily: ds.fontBody,
  fontSize: 13,
  color: ds.textSecondary,
  borderBottom: `1px solid ${ds.borderNormal}`,
  whiteSpace: 'nowrap',
};

export const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search, roleFilter],
    queryFn: () => userService.getUsers({ page, limit: 10, search, role: roleFilter || undefined }),
  });

  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) => userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowEditModal(false);
      setSelectedUser(null);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => userService.toggleUserStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleCreateUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: CreateUserData = {
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      role: formData.get('role') as string,
    };
    createUserMutation.mutate(data);
  };

  const handleUpdateUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedUser) return;
    const formData = new FormData(e.currentTarget);
    const data: UpdateUserData = {
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      email: formData.get('email') as string,
    };
    updateUserMutation.mutate({ id: selectedUser.id, data });
  };

  const modalOverlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  };

  const modalBox: React.CSSProperties = {
    background: ds.card,
    border: `1px solid ${ds.borderSubtle}`,
    borderRadius: 8,
    padding: 28,
    width: '100%',
    maxWidth: 440,
  };

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={{
          fontFamily: ds.fontHeading,
          fontSize: 28,
          fontWeight: 400,
          color: ds.textPrimary,
          margin: 0,
        }}>
          User Management
        </h1>
        <PermissionGuard permission={PERMISSIONS.USERS.CREATE}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '9px 20px',
              background: `linear-gradient(135deg, ${ds.gold} 0%, ${ds.goldHover} 100%)`,
              border: 'none',
              borderRadius: 4,
              color: '#1c2033',
              fontFamily: ds.fontLabel,
              fontSize: 9,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Add User
          </button>
        </PermissionGuard>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ ...inputStyle, width: 'auto', minWidth: 160 }}
        >
          <option value="">All Roles</option>
          {Object.values(UserRole).map((role) => (
            <option key={role} value={role}>
              {role.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={{
        background: ds.card,
        border: `1px solid ${ds.borderNormal}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: ds.elevated }}>
              <tr>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: ds.textMuted }}>
                    Loading...
                  </td>
                </tr>
              ) : data?.users.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: ds.textMuted }}>
                    No users found
                  </td>
                </tr>
              ) : (
                data?.users.map((user: User) => (
                  <tr key={user.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = ds.elevated)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={tdStyle}>
                      <span style={{ color: ds.textPrimary, fontWeight: 500 }}>
                        {user.firstName} {user.lastName}
                      </span>
                    </td>
                    <td style={tdStyle}>{user.email}</td>
                    <td style={tdStyle}>
                      <span style={roleBadgeStyle(user.role)}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 20,
                        background: user.isActive ? 'rgba(80,200,120,0.12)' : 'rgba(220,80,80,0.12)',
                        color: user.isActive ? '#80d0a0' : '#e08080',
                        fontFamily: ds.fontLabel,
                        fontSize: 9,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <PermissionGuard permission={PERMISSIONS.USERS.UPDATE}>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontFamily: ds.fontBody,
                              fontSize: 12,
                              color: ds.gold,
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            Edit
                          </button>
                        </PermissionGuard>
                        <PermissionGuard permission={PERMISSIONS.USERS.UPDATE}>
                          <button
                            onClick={() => toggleStatusMutation.mutate(user.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontFamily: ds.fontBody,
                              fontSize: 12,
                              color: user.isActive ? '#e0a860' : '#80d0a0',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: ds.fontBody, fontSize: 12, color: ds.textMuted }}>
            Showing {data.users.length} of {data.pagination.total} users
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '7px 16px',
                background: 'transparent',
                border: `1px solid ${ds.borderSubtle}`,
                borderRadius: 4,
                color: ds.textSecondary,
                fontFamily: ds.fontBody,
                fontSize: 12,
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                opacity: page === 1 ? 0.4 : 1,
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              style={{
                padding: '7px 16px',
                background: 'transparent',
                border: `1px solid ${ds.borderSubtle}`,
                borderRadius: 4,
                color: ds.textSecondary,
                fontFamily: ds.fontBody,
                fontSize: 12,
                cursor: page === data.pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: page === data.pagination.totalPages ? 0.4 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <h2 style={{
              fontFamily: ds.fontHeading,
              fontSize: 22,
              fontWeight: 400,
              color: ds.textPrimary,
              margin: '0 0 20px',
            }}>
              Create New User
            </h2>
            <form onSubmit={handleCreateUser}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input type="text" name="firstName" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input type="text" name="lastName" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" name="email" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Password</label>
                  <input type="password" name="password" required minLength={6} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Role</label>
                  <select name="role" required style={inputStyle}>
                    {Object.values(UserRole).map((role) => (
                      <option key={role} value={role}>
                        {role.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    background: 'transparent',
                    border: `1px solid ${ds.borderSubtle}`,
                    borderRadius: 4,
                    color: ds.textSecondary,
                    fontFamily: ds.fontLabel,
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isPending}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    background: `linear-gradient(135deg, ${ds.gold} 0%, ${ds.goldHover} 100%)`,
                    border: 'none',
                    borderRadius: 4,
                    color: '#1c2033',
                    fontFamily: ds.fontLabel,
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    cursor: createUserMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: createUserMutation.isPending ? 0.6 : 1,
                  }}
                >
                  {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <h2 style={{
              fontFamily: ds.fontHeading,
              fontSize: 22,
              fontWeight: 400,
              color: ds.textPrimary,
              margin: '0 0 20px',
            }}>
              Edit User
            </h2>
            <form onSubmit={handleUpdateUser}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    defaultValue={selectedUser.firstName}
                    required
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    defaultValue={selectedUser.lastName}
                    required
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={selectedUser.email}
                    required
                    style={inputStyle}
                  />
                </div>
              </div>
              <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    background: 'transparent',
                    border: `1px solid ${ds.borderSubtle}`,
                    borderRadius: 4,
                    color: ds.textSecondary,
                    fontFamily: ds.fontLabel,
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateUserMutation.isPending}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    background: `linear-gradient(135deg, ${ds.gold} 0%, ${ds.goldHover} 100%)`,
                    border: 'none',
                    borderRadius: 4,
                    color: '#1c2033',
                    fontFamily: ds.fontLabel,
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    cursor: updateUserMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: updateUserMutation.isPending ? 0.6 : 1,
                  }}
                >
                  {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
